import appData from 'common/data/App/index.json';
import { theme } from 'common/theme/app';
import { isRunningInAppWebView } from 'common/components/AppModeDetector';
import Seo from 'components/seo';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';
import React, { useEffect, useState } from 'react';
import { PiCaretDown } from 'react-icons/pi';
import styled from 'styled-components';

const FaqWrapper = styled.div`
  width: 100%;
`;

const FaqSection = styled.div`
  margin-top: 28px;
`;

const FaqItem = styled.div`
  margin-bottom: 12px;
  border-radius: 16px;
  border: 1px solid ${theme.colors.borderColor};
  overflow: hidden;
  background: ${theme.colors.surface};
`;

const FaqQuestion = styled.button`
  width: 100%;
  border: 0;
  text-align: left;
  padding: 20px 22px;
  background-color: #fff;
  color: ${theme.colors.headingColor};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
  
  .icon {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    font-size: 24px;
  }

  &:focus-visible {
    outline: 3px solid ${theme.colors.focusRing};
    outline-offset: -3px;
  }
`;

const FaqAnswer = styled.div`
  padding: ${props => props.isOpen ? '0 20px 20px' : '0 20px'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  
  p {
    text-align: left;
    margin-bottom: 15px;
  }
  
  ul {
    margin-left: 20px;
    margin-bottom: 15px;
  }
  
  li {
    margin-bottom: 8px;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button`
  padding: 10px 18px;
  background-color: ${props => props.active ? theme.colors.primary : theme.colors.surfaceMuted};
  color: ${props => props.active ? '#fff' : '#333'};
  border: 1px solid ${props => props.active ? theme.colors.primary : theme.colors.borderColor};
  border-radius: 999px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? theme.colors.primaryHover : theme.colors.cream};
  }

  &:focus-visible {
    outline: 3px solid ${theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

const SearchBox = styled.div`
  margin-bottom: 30px;
  
  input {
    width: 100%;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 999px;
    font-size: 16px;
    transition: border-color 0.3s ease;
    
    &:focus {
      border-color: ${theme.colors.primary};
      outline: 3px solid ${theme.colors.focusRing};
      outline-offset: 2px;
    }
  }
`;

const faqItems = appData.faqs.map((item) => ({
  id: item.id,
  category: item.category,
  question: item.title,
  answer: item.description,
}));

const isAppModeRequest = (search = '') => {
  const params = new URLSearchParams(search);
  return params.get('app') === 'true';
};

const FAQ = ({ location }) => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [hideChrome, setHideChrome] = useState(() =>
    isAppModeRequest(location?.search || '')
  );

  useEffect(() => {
    if (hideChrome) {
      return;
    }
    if (isAppModeRequest(window.location.search) || isRunningInAppWebView()) {
      setHideChrome(true);
    }
  }, [hideChrome]);
  
  // Extract unique categories
  const categories = ['All', ...new Set(faqItems.map(item => item.category))];
  
  // Toggle FAQ item
  const toggleItem = (id) => {
    setOpenItems(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };
  
  // Filter FAQ items by category and search query
  const filteredItems = faqItems.filter(item => {
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Create structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  
  return (
    <SiteLayout showFooter={!hideChrome} showNavbar={!hideChrome}>
        <Seo
          title="FAQ | Furbuz Pet Care App"
          description="Answers about Furbuz Free, Furbuz Pro, pet limits, Connect, marketplace, private profiles, location, and account deletion."
          structuredData={[structuredData]}
        />

        <ContentPage
          title="Frequently Asked Questions"
          description="Find answers to commonly asked questions about the Furbuz app and pet care services."
          eyebrow="Help center"
          compact={hideChrome}
        >
          <FaqWrapper>
            <SearchBox>
              <input 
                type="text" 
                aria-label="Search frequently asked questions"
                placeholder="Search for questions or keywords..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBox>
            
            <CategoryTabs>
              {categories.map((category) => (
                <CategoryTab 
                  key={category}
                  active={activeCategory === category}
                  aria-pressed={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </CategoryTab>
              ))}
            </CategoryTabs>
            
            <FaqSection>
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <FaqItem key={item.id}>
                    <FaqQuestion 
                      aria-expanded={Boolean(openItems[item.id])}
                      aria-controls={`faq-answer-${item.id}`}
                      onClick={() => toggleItem(item.id)}
                      isOpen={openItems[item.id]}
                    >
                      <h3>{item.question}</h3>
                      <PiCaretDown className="icon" aria-hidden="true" />
                    </FaqQuestion>
                    <FaqAnswer
                      id={`faq-answer-${item.id}`}
                      isOpen={openItems[item.id]}
                      aria-hidden={!openItems[item.id]}
                      role="region"
                    >
                      <p>{item.answer}</p>
                    </FaqAnswer>
                  </FaqItem>
                ))
              ) : (
                <p style={{ textAlign: 'center', marginTop: '40px' }}>
                  No results found. Please try another search or category.
                </p>
              )}
            </FaqSection>
          </FaqWrapper>
        </ContentPage>
    </SiteLayout>
  );
};

export default FAQ; 